import {
  Clock,
  Mail,
  MapPin,
  MoveRight,
  Phone,
  CircleCheck,
} from "lucide-react";
import { images } from "../assets/assets";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import React, { useState } from "react";
import { createAppointment } from "../api";

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
    type: "contact",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Name must be at least 3 characters";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!validateForm()) {
      console.log("❌ Form validation failed:", errors);
      return;
    }

    setSubmitting(true);
    try {
      // Prepare contact data to send to API
      const contactData = {
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        message: formData.message,
        type: formData.type,
      };

      console.log("✅ Form Data Submitted:", contactData);
      const response = await createAppointment(contactData);

      // Show success message from API response
      setSuccessMessage(response?.message || "Message sent successfully!");
      console.log("Form submission successful!");

      // Reset form
      setFormData({
        fullName: "",
        phone: "",
        email: "",
        message: "",
        type: "contact",
      });
    } catch (error) {
      // Show error message
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send message. Please try again.";
      setErrorMessage(errorMsg);
      console.error("❌ Error sending message:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Success Modal
  const SuccessModal = () => (
    <>
      {successMessage && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[20px] p-6 sm:p-8 max-w-[400px] w-full shadow-2xl animate-scale-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CircleCheck color="#22c55e" className="w-8 h-8" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Success!
              </h2>
              <p className="text-gray-600 mb-6">{successMessage}</p>
              <button
                onClick={() => setSuccessMessage("")}
                className="w-full bg-[#1975FF] text-white py-2 sm:py-3 rounded-[10px] font-semibold hover:bg-[#1560d4] transition-all duration-200"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Error Modal
  const ErrorModal = () => (
    <>
      {errorMessage && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[20px] p-6 sm:p-8 max-w-[400px] w-full shadow-2xl animate-scale-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <p className="text-2xl font-bold text-red-500">!</p>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Error
              </h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <button
                onClick={() => setErrorMessage("")}
                className="w-full bg-red-500 text-white py-2 sm:py-3 rounded-[10px] font-semibold hover:bg-red-600 transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="font-lato">
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
      <SuccessModal />
      <ErrorModal />
      <Navbar />
      {/* Top Page heading banner */}
      <div className="bg-[#F7F7F7B2] py-4 sm:py-6 flex justify-center px-4">
        <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-2 sm:gap-4 items-center max-w-[1100px]">
          <img
            src={images.Logo}
            alt="logo"
            className="w-[50px] h-[50px] sm:w-[80px] sm:h-[80px] md:w-[95px] md:h-[95px] flex-shrink-0"
          />
          <div className="max-w-[500px]">
            <p className="font-bold text-[14px] sm:text-[18px] md:text-[22px] text-center sm:text-left">
              Devrishi-Contact
            </p>
            <p className="font-openSans text-[11px] sm:text-[13px] md:text-[16px] leading-[1.2] text-center sm:text-left">
              We are here to assist you with all your healthcare needs. Contact
              us for appointments, emergency services, or any medical inquiries.
            </p>
          </div>
        </div>
      </div>
      {/* Info */}
      <div className="flex flex-wrap gap-6 items-center max-w-[1000px] mx-auto my-3 justify-between px-4">
        <div className="flex gap-1 items-center">
          <MapPin color="#000" className="w-5 h-5" />
          <p className="text-[12px] sm:text-[13px] font-semibold w-full sm:max-w-[150px] leading-[1.2]">
            3rd, KM Milestone Road, Sarsawa, Nakur Uttar Pradesh 247342
          </p>
        </div>
        <div className="flex gap-1 items-center">
          <Phone color="#000" className="w-5 h-5 mt-0.5" />
          <a
            href="tel:7300906444"
            className="text-[12px] sm:text-[13px] font-semibold hover:text-[#1975FF] transition-all duration-200"
          >
            7300906444
          </a>
        </div>
        <div className="flex gap-1 items-center ">
          <Mail color="#000" className="w-5 h-5 mt-0.5" />
          <a
            href="mailto:hospitaldevrishi@gmail.com"
            className="text-[12px] sm:text-[13px] font-semibold hover:text-[#1975FF] transition-all duration-200"
          >
            hospitaldevrishi@gmail.com
          </a>
        </div>
        <div className="flex gap-1 items-center">
          <Clock color="#000" className="w-5 h-5 mt-0.5" />
          <p className="text-[12px] sm:text-[13px] font-semibold">Open 24×7</p>
        </div>
      </div>

      {/* Form */}
      <div className="my-6 sm:my-8 md:my-10 px-4">
        <div
          className="w-full sm:w-[95%] lg:w-[90%] mx-auto min-h-[500px] sm:min-h-[700px] lg:min-h-[600px] rounded-[20px] bg-cover bg-center flex items-center justify-center md:justify-end p-4 sm:p-10 lg:p-12"
          style={{ backgroundImage: `url(${images.ContactPageBanner})` }}
        >
          <div className="w-full sm:w-[90%] md:w-[85%] lg:w-[500px] py-5 sm:py-8 px-5 sm:px-8 bg-white rounded-[20px] shadow-[14px_9px_15.3px_5px_#0000001A]">
            <h1 className="font-bold text-[16px] sm:text-[20px] md:text-[25px] uppercase text-black text-left leading-[1.2]">
              Send Us a Message
            </h1>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="name"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.fullName ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="Enter your full name"
              />
              {errors.fullName && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.fullName}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="phone"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Phone Number
              </label>
              <input
                type="text"
                id="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.phone ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="Enter your Phone Number"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.phone}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="email"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.email ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="your.email@gmail.com"
              />
              {errors.email && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="message"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Message
              </label>
              <textarea
                id="message"
                rows={3}
                value={formData.message}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base resize-none transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.message ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="Type your message here......."
              />
              {errors.message && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.message}
                </p>
              )}
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-fit mx-auto mt-5 bg-[#1975FF] rounded-[8px] sm:rounded-[10px] py-2 sm:py-2.5 pr-2 pl-3 sm:pl-4 flex items-center justify-center gap-2 text-[13px] sm:text-[14px] font-semibold text-white transition-all duration-200 shadow-md group focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-2 ${
                submitting
                  ? "opacity-70 cursor-not-allowed bg-[#1975FF]"
                  : "hover:bg-[#1560d4] hover:shadow-lg hover:-translate-y-0.5 active:bg-[#1050b8] active:scale-95 active:shadow-md"
              }`}
            >
              {submitting ? "Sending..." : "Send Message"}{" "}
              <span
                className={`p-2 bg-white rounded-full transition-transform duration-300 ${!submitting && "group-hover:scale-110"}`}
              >
                <MoveRight
                  color="#000"
                  className="rotate-[-38deg] w-3.5 h-3.5 sm:w-4 sm:h-4"
                />
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="w-full sm:w-[95%] md:w-[95%] lg:w-[90%] mx-auto px-2 sm:px-4">
        <p className="font-poppins font-medium text-[18px] sm:text-[20px] md:text-[22px] text-black my-2 w-[95%] sm:w-full mx-auto">
          Find Us on Map
        </p>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55316.31070675089!2d77.29474974105047!3d29.942928169546594!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ef3f7dee3df79%3A0x8e81b8ce7be9da58!2sDev%20Rishi%20Hospital%20%26%20Research%20Centre!5e0!3m2!1sen!2sin!4v1776407509565!5m2!1sen!2sin"
          allowfullscreen=""
          loading="lazy"
          className="w-[96%] mx-auto h-[350px] sm:h-[380px] md:h-[420px] rounded-[30px] sm:rounded-[40px] md:rounded-[50px] shadow-[0px_0px_12px_0px_#0000000D] px-2 sm:px-0"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
      {/*  Emergency CTA  */}
      <div className="py-10 md:py-12 bg-[#F7F7F7B2] px-4 mb-5 mt-4 sm:mt-6 sm:mb-8 md:mb-10 md:mt-8">
        <h1 className="max-w-[550px] text-center text-[22px] md:text-[32px] font-semibold mx-auto leading-[1.2]">
          Emergency?
        </h1>
        <p className="max-w-[480px] font-openSans text-[15px] md:text-[18px] mx-auto text-center mt-3">
          For immediate medical assistance, call our emergency helpline now.
        </p>
        <div className="flex justify-center mt-8">
          <a
            href="tel:+917300906444"
            className="bg-[#E00F1D] rounded-[10px] py-1 pr-1 pl-4 flex items-center gap-2 text-[14px] font-semibold text-white hover:bg-[#b80d18] active:bg-[#900b13] active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg group"
          >
            <Phone color="#ffffff" className="w-4 h-4" />
            +91 7300906444
            <span className="p-2 bg-white rounded-full transition-transform duration-300 group-hover:scale-110">
              <MoveRight color="#000" className="rotate-[-38deg] w-4 h-4" />
            </span>
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
